import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Inject, Input, OnDestroy, OnInit, inject } from '@angular/core';

import { OddsConfig } from '@frontend/sports/common/client-config-data-access';
import { SimpleCallback } from '@frontend/sports/common/core/utils/extended-types';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { CalculatedOdds, OddsFormatterService, OddsOperations } from '@frontend/sports/odds/feature';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { UserOddsFormatSettingsService } from '@frontend/sports/user/feature';
import { Subscription } from 'rxjs';

import { oddsBoostAnimations } from '../../../../reward-tokens/components/digital/odds-boost-animations';
import { ILinearContainerSummaryService, LINEAR_CONTAINER_SUMMARY_SERVICE } from '../../../services/linear/linear-container-summary.interface';

@Component({
    selector: 'bs-linear-container-total-odds',
    templateUrl: './linear-container-total-odds.component.html',
    styleUrls: ['./linear-container-total-odds.component.scss'],
    animations: [oddsBoostAnimations],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export default class LinearContainerTotalOddsComponent implements OnInit, OnDestroy {
    @Input() oddsDisplayFormat: OddsDisplayFormat;
    @Input() indicateOddsChange: boolean;

    @HostBinding('class') class = 'reward-tokens-module';

    private readonly INCREASED_CLASS = 'increased';
    private readonly DECREASED_CLASS = 'decreased';

    private summaryChangesSubscription?: Subscription;
    private clearInterval?: SimpleCallback;
    private removeOddsAnimationCallback?: SimpleCallback = undefined;
    private previousOdds: CalculatedOdds | null;

    totalOdds: CalculatedOdds | null;
    boostedOdds: CalculatedOdds | null;
    oddsChangeIndicator = '';
    private readonly userOddsFormatSettingsService = inject(UserOddsFormatSettingsService);

    constructor(
        private oddsFormatter: OddsFormatterService,
        private timerService: TimerService,
        private oddsConfig: OddsConfig,
        private changeRef: ChangeDetectorRef,
        @Inject(LINEAR_CONTAINER_SUMMARY_SERVICE) private summaryService: ILinearContainerSummaryService,
    ) {}

    get userFormat() {
        return OddsOperations.convertToBposOddsFormat(this.userOddsFormatSettingsService.oddsFormat);
    }

    ngOnInit(): void {
        this.summaryChangesSubscription = this.summaryService.getSummary$().subscribe((value) => {
            if (!value) {
                return;
            }

            if (!value.totalOdds) {
                this.updateOdds(value.totalOdds ?? null, value.boostedOdds ?? null);

                return;
            }

            if (this.indicateOddsChange && value.hasOddsUpdate) {
                this.setOddsChangeIndicator(value.totalOdds);
            }

            this.updateOdds(value.totalOdds, value.boostedOdds ?? null);
        });
    }

    ngOnDestroy(): void {
        this.summaryChangesSubscription?.unsubscribe();
    }

    get isValidOdds(): boolean {
        if (!this.totalOdds) {
            return false;
        }

        const oddsFormat = this.userOddsFormatSettingsService.oddsFormat ?? OddsDisplayFormat.EU;

        switch (oddsFormat) {
            case OddsDisplayFormat.EU:
                return this.totalOdds.decimals.toNumber() !== 0;

            case OddsDisplayFormat.UK:
                return !this.totalOdds.fractional.isEmpty();

            case OddsDisplayFormat.US:
                return this.totalOdds.moneyline.toNumber() !== 0;

            default:
                return this.totalOdds.decimals.toNumber() !== 0;
        }
    }

    setOddsChangeIndicator(newOdds: CalculatedOdds): void {
        this.clearInterval?.();

        if (!this.previousOdds?.decimals?.toNumber()) {
            this.removeClass();

            return;
        }

        if (OddsOperations.greaterThan(newOdds, this.previousOdds, this.userFormat)) {
            this.handleOddsChange(this.INCREASED_CLASS);
        }

        if (OddsOperations.lessThan(newOdds, this.previousOdds, this.userFormat)) {
            this.handleOddsChange(this.DECREASED_CLASS);
        }
    }

    private updateOdds(odds: CalculatedOdds | null, boostedOdds: CalculatedOdds | null): void {
        this.previousOdds = odds;
        this.totalOdds = odds;
        this.boostedOdds = boostedOdds;
        this.changeRef.markForCheck();
    }

    private handleOddsChange(className: string): void {
        this.removeOddsAnimationCallback?.();
        this.scheduleRemoveClass();
        this.addClass(className);
    }

    private addClass(className: string): void {
        this.clearInterval = this.timerService.setTimeout(() => (this.oddsChangeIndicator = className));
    }

    private removeClass(): void {
        this.oddsChangeIndicator = '';
    }

    private scheduleRemoveClass(): void {
        this.removeOddsAnimationCallback = this.timerService.setTimeout(
            () => this.removeClass(),
            this.oddsConfig.display.oddsIndicatorAnimationDuration,
        );
    }

    formattedOdds(odds: CalculatedOdds | null): string {
        return this.oddsFormatter.format(odds, { displayFormat: this.oddsDisplayFormat, isSummary: true });
    }
}
